import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exchangeCodeForTokens, parseIdToken } from '../services/oauth';
import { useAuth } from '../contexts/AuthContext';

const OAuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithTokens } = useAuth();
  const [status, setStatus] = useState('Processing sign-in...');
  const [error, setError] = useState(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    const errorDesc = searchParams.get('error_description');

    if (errorParam) {
      setError(errorDesc || errorParam);
      return;
    }

    if (!code) {
      setError('No authorization code received. Please try signing in again.');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setStatus('Exchanging authorization code...');
        const tokens = await exchangeCodeForTokens(code);

        if (cancelled) return;

        const userInfo = parseIdToken(tokens.idToken);
        setStatus('Signing you in...');

        if (loginWithTokens) {
          await loginWithTokens(tokens, userInfo);
        } else {
          localStorage.setItem('idToken', tokens.idToken);
          localStorage.setItem('accessToken', tokens.accessToken);
          if (tokens.refreshToken) {
            localStorage.setItem('refreshToken', tokens.refreshToken);
          }
          if (userInfo) {
            localStorage.setItem('user', JSON.stringify({
              email: userInfo.email,
              name: userInfo.name,
              id: userInfo.sub,
              provider: userInfo.provider,
            }));
          }
        }

        if (!cancelled) {
          navigate('/profile', { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          console.error('OAuth callback error:', err);
          setError(err.message || 'Authentication failed. Please try again.');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [searchParams, navigate, loginWithTokens]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">✕</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Sign-In Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="btn btn-primary w-full"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{status}</h2>
        <p className="text-gray-500 text-sm">Please wait while we complete your sign-in.</p>
      </div>
    </div>
  );
};

export default OAuthCallbackPage;
